import React, { useEffect, useMemo, useState } from "react";
import {
    Package,
    Plus,
    Edit3,
    Trash2,
    RefreshCw,
    Loader2,
    Search,
    X,
    Save,
    Eye,
    EyeOff,
    Euro,
} from "lucide-react";
import { toast } from "sonner";
import ImageInput from "./ImageInput";

const NUTRI_FIELDS = [
    ["energia_kcal", "Energia (kcal)"],
    ["hidratos_g", "Hidratos (g)"],
    ["acucares_g", "Açúcares (g)"],
    ["fibra_g", "Fibra (g)"],
    ["proteina_g", "Proteína (g)"],
    ["vitamina_c_mg", "Vitamina C (mg)"],
];

const EMPTY = {
    id: "",
    name: "",
    description: "",
    price: "",
    color: "#7A8B76",
    image: "",
    active: true,
    badge: "",
    harvest_date: "",
    freeze_date: "",
    uses_text: "",
    nutrition: {},
};

export default function AdminProducts({ apiAuth }) {
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(true);
    const [search, setSearch] = useState("");
    const [editing, setEditing] = useState(null); // null | "new" | obj

    const load = async () => {
        setBusy(true);
        try {
            const r = await apiAuth("/admin/catalog/products");
            setItems(r.data || []);
        } catch {
            toast.error("Erro ao carregar produtos");
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter((i) =>
            `${i.name} ${i.id}`.toLowerCase().includes(q),
        );
    }, [items, search]);

    const activeCount = items.filter((i) => i.active !== false).length;

    const handleDelete = async (item) => {
        if (
            !window.confirm(
                `Apagar definitivamente "${item.name}"? Esta ação é irreversível e remove também o stock associado.`,
            )
        )
            return;
        try {
            await apiAuth(`/admin/catalog/products/${item.id}`, {
                method: "DELETE",
            });
            toast.success(`${item.name} apagado`);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erro a apagar");
        }
    };

    const handleToggleActive = async (item) => {
        try {
            await apiAuth(`/admin/catalog/products/${item.id}`, {
                method: "PUT",
                data: { active: item.active === false },
            });
            toast.success(
                item.active === false
                    ? "Produto ativado (visível na loja)"
                    : "Produto oculto da loja",
            );
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erro");
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-products-panel">
            <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat
                    icon={<Package className="h-5 w-5 text-brand-green" />}
                    label="Total"
                    value={items.length}
                />
                <Stat
                    tint="green"
                    icon={<Eye className="h-5 w-5 text-brand-green" />}
                    label="Ativos"
                    value={activeCount}
                />
                <Stat
                    tint="orange"
                    icon={<EyeOff className="h-5 w-5 text-brand-orange" />}
                    label="Ocultos"
                    value={items.length - activeCount}
                />
                <button
                    type="button"
                    onClick={() => setEditing("new")}
                    data-testid="btn-new-product"
                    className="rounded-3xl bg-brand-green hover:bg-brand-green-dark text-white p-5 shadow-pop transition-colors flex flex-col items-start justify-between text-left"
                >
                    <Plus className="h-5 w-5" />
                    <div className="mt-3 text-[11px] uppercase tracking-widest font-bold text-white/85">
                        Adicionar
                    </div>
                    <div className="mt-1 font-display font-medium text-2xl">
                        Novo produto
                    </div>
                </button>
            </section>

            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[240px] max-w-xl">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-ink-soft" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Pesquisar produtos…"
                        data-testid="admin-products-search"
                        className="w-full rounded-full bg-white border-2 border-brand-green/15 focus:border-brand-green px-10 py-2.5 text-sm font-medium text-brand-ink placeholder:text-brand-ink-soft focus:outline-none transition-colors"
                    />
                </div>
                <button
                    type="button"
                    onClick={load}
                    className="inline-flex items-center gap-1.5 rounded-full bg-white hover:bg-brand-green-soft text-brand-ink px-3 py-2 text-xs font-semibold transition-colors ml-auto"
                    data-testid="admin-products-refresh"
                >
                    <RefreshCw
                        className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`}
                    />
                    Atualizar
                </button>
            </div>

            <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {busy ? (
                    <div className="col-span-full flex justify-center p-16">
                        <Loader2 className="h-6 w-6 animate-spin text-brand-green" />
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="col-span-full rounded-3xl bg-white shadow-soft ring-1 ring-brand-green/10 p-16 text-center">
                        <Package className="h-10 w-10 text-brand-green/40 mx-auto" />
                        <p className="mt-4 text-sm text-brand-ink-soft">
                            {items.length === 0
                                ? "Ainda não há produtos. Adiciona o primeiro!"
                                : "Nenhum produto corresponde à pesquisa."}
                        </p>
                    </div>
                ) : (
                    filtered.map((p) => (
                        <article
                            key={p.id}
                            data-testid={`admin-product-${p.id}`}
                            className={`rounded-3xl bg-white shadow-soft ring-1 ring-brand-green/10 overflow-hidden flex flex-col transition-opacity ${
                                p.active === false ? "opacity-60" : ""
                            }`}
                        >
                            <div
                                className="aspect-video relative overflow-hidden"
                                style={{ backgroundColor: p.color || "#FFF" }}
                            >
                                {p.image ? (
                                    <img
                                        src={p.image}
                                        alt={p.name}
                                        className="absolute inset-0 w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <Package className="h-10 w-10 text-white/70" />
                                    </div>
                                )}
                                {p.active === false && (
                                    <span className="absolute top-3 left-3 text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 bg-brand-ink/90 text-white inline-flex items-center gap-1">
                                        <EyeOff className="h-2.5 w-2.5" />
                                        Oculto
                                    </span>
                                )}
                            </div>
                            <div className="p-4 flex-1 flex flex-col">
                                <h3 className="font-display font-medium text-brand-ink">
                                    {p.name}
                                </h3>
                                <p className="text-xs text-brand-ink-soft mt-1 flex-1 line-clamp-2">
                                    {p.description}
                                </p>
                                <div className="mt-3 flex items-center justify-between">
                                    <span className="inline-flex items-center gap-1 font-display font-medium text-xl text-brand-green">
                                        <Euro className="h-4 w-4" />
                                        {Number(p.price).toFixed(2)}
                                    </span>
                                    <span className="text-[10px] uppercase tracking-widest font-bold text-brand-ink-soft">
                                        {p.id}
                                    </span>
                                </div>
                                <div className="mt-3 flex items-center gap-2 pt-3 border-t border-brand-green/10">
                                    <button
                                        type="button"
                                        onClick={() => handleToggleActive(p)}
                                        data-testid={`btn-toggle-product-${p.id}`}
                                        title={
                                            p.active === false
                                                ? "Mostrar na loja"
                                                : "Ocultar da loja"
                                        }
                                        className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                                            p.active === false
                                                ? "bg-brand-cream text-brand-ink hover:bg-brand-ink hover:text-white"
                                                : "bg-brand-green-soft text-brand-green hover:bg-brand-green hover:text-white"
                                        }`}
                                    >
                                        {p.active === false ? (
                                            <EyeOff className="h-3 w-3" />
                                        ) : (
                                            <Eye className="h-3 w-3" />
                                        )}
                                        {p.active === false ? "Oculto" : "Ativo"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setEditing(p)}
                                        data-testid={`btn-edit-product-${p.id}`}
                                        className="inline-flex items-center gap-1 rounded-full bg-white hover:bg-brand-green-soft border border-brand-green/15 text-brand-ink px-3 py-1.5 text-xs font-semibold transition-colors"
                                    >
                                        <Edit3 className="h-3 w-3" /> Editar
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleDelete(p)}
                                        data-testid={`btn-delete-product-${p.id}`}
                                        className="ml-auto inline-flex items-center gap-1 rounded-full bg-white hover:bg-red-500 border border-red-200 hover:border-red-500 text-red-500 hover:text-white px-3 py-1.5 text-xs font-semibold transition-colors"
                                    >
                                        <Trash2 className="h-3 w-3" />
                                    </button>
                                </div>
                            </div>
                        </article>
                    ))
                )}
            </section>

            {editing && (
                <ProductDialog
                    apiAuth={apiAuth}
                    initial={editing === "new" ? null : editing}
                    onClose={() => setEditing(null)}
                    onSaved={async () => {
                        setEditing(null);
                        await load();
                    }}
                />
            )}
        </div>
    );
}

function ProductDialog({ apiAuth, initial, onClose, onSaved }) {
    const isNew = !initial;
    const [form, setForm] = useState(() =>
        initial
            ? {
                  ...EMPTY,
                  ...initial,
                  price: initial.price ?? "",
                  active: initial.active !== false,
                  harvest_date: initial.harvest_date || "",
                  freeze_date: initial.freeze_date || "",
                  uses_text: (initial.uses || []).join("\n"),
                  nutrition: initial.nutrition || {},
              }
            : { ...EMPTY },
    );
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const setNutri = (k, v) =>
        setForm((f) => ({ ...f, nutrition: { ...f.nutrition, [k]: v } }));

    const submit = async (e) => {
        e.preventDefault();
        const price = parseFloat(form.price);
        if (Number.isNaN(price) || price < 0) {
            toast.error("Preço inválido");
            return;
        }
        const nutrition = {};
        for (const [k] of NUTRI_FIELDS) {
            const raw = form.nutrition?.[k];
            if (raw !== "" && raw !== null && raw !== undefined) {
                const n = parseFloat(raw);
                if (!Number.isNaN(n)) nutrition[k] = n;
            }
        }
        const uses = (form.uses_text || "")
            .split("\n")
            .map((s) => s.trim())
            .filter(Boolean);
        const payload = {
            name: form.name.trim(),
            description: form.description?.trim() || "",
            price,
            color: form.color?.trim() || "#7A8B76",
            image: form.image?.trim() || "",
            active: !!form.active,
            badge: form.badge?.trim() || "",
            harvest_date: form.harvest_date || "",
            freeze_date: form.freeze_date || "",
            uses: uses,
            nutrition: nutrition,
        };
        setSaving(true);
        try {
            if (isNew) {
                const pid = form.id?.trim();
                await apiAuth("/admin/catalog/products", {
                    method: "POST",
                    data: pid ? { ...payload, id: pid } : payload,
                });
                toast.success("Produto criado");
            } else {
                await apiAuth(`/admin/catalog/products/${initial.id}`, {
                    method: "PUT",
                    data: payload,
                });
                toast.success("Produto atualizado");
            }
            await onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erro a guardar");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
            onClick={onClose}
            data-testid="product-dialog"
        >
            <form
                onClick={(e) => e.stopPropagation()}
                onSubmit={submit}
                className="w-full max-w-3xl my-8 rounded-3xl bg-white shadow-2xl overflow-hidden"
            >
                <div className="px-6 py-4 bg-brand-green text-white flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Package className="h-5 w-5" />
                        <h2 className="font-display font-medium text-lg">
                            {isNew ? "Novo produto" : "Editar produto"}
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-white/15 transition-colors"
                        data-testid="product-dialog-close"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="p-6 grid md:grid-cols-[1fr_280px] gap-6 max-h-[75vh] overflow-y-auto">
                    <div className="space-y-4">
                        <div className="grid md:grid-cols-2 gap-3">
                            <Field label="Nome *">
                                <input
                                    required
                                    type="text"
                                    value={form.name}
                                    onChange={(e) => set("name", e.target.value)}
                                    className={inputCls}
                                    data-testid="product-field-name"
                                />
                            </Field>
                            <Field label="Preço (€) *">
                                <input
                                    required
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={form.price}
                                    onChange={(e) => set("price", e.target.value)}
                                    className={inputCls}
                                    data-testid="product-field-price"
                                />
                            </Field>
                        </div>
                        <Field label="Descrição">
                            <textarea
                                rows={3}
                                value={form.description}
                                onChange={(e) => set("description", e.target.value)}
                                className={`${inputCls} resize-none`}
                                data-testid="product-field-desc"
                            />
                        </Field>
                        <div className="grid md:grid-cols-2 gap-3">
                            <Field
                                label="Cor (hex)"
                                hint="Usada em badges e cards. Ex: #B95C50"
                            >
                                <div className="flex items-center gap-2">
                                    <input
                                        type="color"
                                        value={form.color || "#7A8B76"}
                                        onChange={(e) => set("color", e.target.value)}
                                        className="h-10 w-12 rounded-xl border-2 border-brand-green/15 cursor-pointer"
                                        data-testid="product-field-color-picker"
                                    />
                                    <input
                                        type="text"
                                        value={form.color}
                                        onChange={(e) => set("color", e.target.value)}
                                        className={inputCls}
                                        data-testid="product-field-color"
                                    />
                                </div>
                            </Field>
                            {isNew && (
                                <Field
                                    label="ID (opcional)"
                                    hint="Se vazio, é gerado a partir do nome. Ex: kiwi, pessego"
                                >
                                    <input
                                        type="text"
                                        value={form.id}
                                        onChange={(e) =>
                                            set(
                                                "id",
                                                e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"),
                                            )
                                        }
                                        className={inputCls}
                                        data-testid="product-field-id"
                                    />
                                </Field>
                            )}
                        </div>
                        <div className="grid md:grid-cols-2 gap-3">
                            <Field label="Selo (badge)" hint="Ex: Mais vendido, Novidade — vazio para nenhum">
                                <input
                                    value={form.badge || ""}
                                    onChange={(e) => set("badge", e.target.value)}
                                    className={inputCls}
                                    data-testid="product-field-badge"
                                />
                            </Field>
                            <div />
                        </div>
                        <div className="grid md:grid-cols-2 gap-3">
                            <Field label="Data de colheita" hint="Selo de frescura">
                                <input
                                    type="date"
                                    value={form.harvest_date}
                                    onChange={(e) => set("harvest_date", e.target.value)}
                                    className={inputCls}
                                    data-testid="product-field-harvest-date"
                                />
                            </Field>
                            <Field label="Data de congelação" hint="Idealmente igual à colheita">
                                <input
                                    type="date"
                                    value={form.freeze_date}
                                    onChange={(e) => set("freeze_date", e.target.value)}
                                    className={inputCls}
                                    data-testid="product-field-freeze-date"
                                />
                            </Field>
                        </div>
                        <Field
                            label="Sugestões de uso"
                            hint="Uma sugestão por linha (aparecem na página do sabor)"
                        >
                            <textarea
                                rows={3}
                                value={form.uses_text}
                                onChange={(e) => set("uses_text", e.target.value)}
                                className={`${inputCls} resize-none`}
                                data-testid="product-field-uses"
                            />
                        </Field>
                        <Field label="Perfil nutricional (por 100 ml)">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {NUTRI_FIELDS.map(([k, label]) => (
                                    <div key={k}>
                                        <div className="text-[10px] uppercase tracking-wider font-bold text-brand-ink-soft mb-1">
                                            {label}
                                        </div>
                                        <input
                                            type="number"
                                            step="0.1"
                                            min="0"
                                            value={form.nutrition?.[k] ?? ""}
                                            onChange={(e) => setNutri(k, e.target.value)}
                                            className={inputCls}
                                            data-testid={`product-field-nutrition-${k}`}
                                        />
                                    </div>
                                ))}
                            </div>
                        </Field>
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={!!form.active}
                                onChange={(e) => set("active", e.target.checked)}
                                className="h-4 w-4 rounded border-brand-green/30 text-brand-green focus:ring-brand-green"
                                data-testid="product-field-active"
                            />
                            <span className="text-sm font-semibold text-brand-ink">
                                Visível na loja pública
                            </span>
                        </label>
                    </div>
                    <div>
                        <ImageInput
                            value={form.image}
                            onChange={(v) => set("image", v)}
                            label="Imagem do produto"
                            testid="product-field-image"
                        />
                    </div>
                </div>

                <div className="px-6 py-4 bg-brand-cream/60 border-t border-brand-green/10 flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex items-center gap-1.5 rounded-full bg-white hover:bg-brand-cream border border-brand-green/15 text-brand-ink px-4 py-2 text-xs font-semibold transition-colors"
                        data-testid="product-dialog-cancel"
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="inline-flex items-center gap-1.5 rounded-full bg-brand-green hover:bg-brand-green-dark disabled:opacity-50 text-white px-5 py-2 text-xs font-bold transition-colors"
                        data-testid="product-dialog-save"
                    >
                        {saving ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <Save className="h-3.5 w-3.5" />
                        )}
                        {isNew ? "Criar produto" : "Guardar alterações"}
                    </button>
                </div>
            </form>
        </div>
    );
}

// ============================================================
// Shared small components
// ============================================================
export const inputCls =
    "w-full rounded-xl bg-white border-2 border-brand-green/15 focus:border-brand-green px-3 py-2 text-sm font-medium text-brand-ink placeholder:text-brand-ink-soft focus:outline-none transition-colors";

export const Field = ({ label, hint, children }) => (
    <label className="block">
        <span className="text-[11px] uppercase tracking-widest font-bold text-brand-ink-soft">
            {label}
        </span>
        <div className="mt-1.5">{children}</div>
        {hint && (
            <span className="mt-1 block text-[11px] text-brand-ink-soft italic">
                {hint}
            </span>
        )}
    </label>
);

const Stat = ({ icon, label, value, tint }) => {
    const bg =
        tint === "green"
            ? "bg-brand-green-soft"
            : tint === "orange"
                ? "bg-brand-orange-soft"
                : "bg-white";
    return (
        <div className={`rounded-3xl ${bg} p-5 shadow-soft ring-1 ring-brand-green/5`}>
            {icon}
            <div className="mt-3 text-[11px] uppercase tracking-widest font-bold text-brand-ink-soft">
                {label}
            </div>
            <div className="mt-1 font-display font-medium text-3xl text-brand-ink">
                {value}
            </div>
        </div>
    );
};
